/**
 * Drawing helpers for the PixiJS table planner (parity with VisualTable layout).
 */

import type { TableConfig } from '@/components/table-planner/TableConfigModal';

/** Floor plan units: 100 = 1 m. Matches TableConfigModal width/height step of 0.1 m. */
export const TABLE_PLANNER_METRIC_STEP = 10;

/** Width of the +/- seat bar in logical px at scale 1 (matches Pixi render). */
export const SEAT_COUNTER_BUTTON_SIZE_PX = 18;

/**
 * On tall narrow tables, left and right horizontal pills share one horizontal band; if the
 * table is too narrow they overlap. Offset one up and one down so they stay on the long edges.
 */
export function longEdgeHorizontalStaggerY(
  width: number,
  height: number,
  scale: number
): number {
  const insetFromEdge = 14 / scale;
  const insetStep = 6 / scale;
  const bs = SEAT_COUNTER_BUTTON_SIZE_PX / scale;
  const leftCx = -width / 2 + insetFromEdge + insetStep;
  const rightCx = width / 2 - insetFromEdge - insetStep;
  const minClear = bs * 2.4 + 8 / scale;
  if (rightCx - leftCx >= minClear) {
    return 0;
  }
  return Math.min(height * 0.14, bs * 5);
}

/**
 * Horizontal [-][+] bar (circular table control below the table): split on X at centre.
 */
function hitAdjustBar(
  localX: number,
  localY: number,
  cx: number,
  cy: number,
  scale: number,
  tableId: string,
  side: string
): { kind: 'adjustCounter'; tableId: string; side: string; delta: -1 | 1 } | null {
  const buttonSize = SEAT_COUNTER_BUTTON_SIZE_PX / scale;
  const hitPad = 8 / scale;
  const halfW = buttonSize * 1.2 + hitPad;
  const halfH = buttonSize / 2 + hitPad;
  if (
    localX < cx - halfW ||
    localX > cx + halfW ||
    localY < cy - halfH ||
    localY > cy + halfH
  ) {
    return null;
  }
  const delta: -1 | 1 = localX <= cx ? -1 : 1;
  return { kind: 'adjustCounter', tableId, side, delta };
}

/**
 * Horizontal bar drawn with local rotation -R (screen-horizontal). Table-local pointer must be
 * rotated by +R before axis-aligned hit testing against the bar.
 */
function hitAdjustBarHorizontalScreenAligned(
  localX: number,
  localY: number,
  cx: number,
  cy: number,
  scale: number,
  tableId: string,
  side: string,
  tableRotationDeg: number
): { kind: 'adjustCounter'; tableId: string; side: string; delta: -1 | 1 } | null {
  const vx = localX - cx;
  const vy = localY - cy;
  const rot = (tableRotationDeg * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const px = vx * cos - vy * sin;
  const py = vx * sin + vy * cos;
  return hitAdjustBar(px, py, 0, 0, scale, tableId, side);
}

/**
 * Vertical [+] above [-] bar: narrow in X, split on Y (upper half = increment, lower = decrement).
 * Matches table-local layout; rotates with the table (no screen-axis counter-rotation).
 */
function hitAdjustBarVertical(
  localX: number,
  localY: number,
  cx: number,
  cy: number,
  scale: number,
  tableId: string,
  side: string
): { kind: 'adjustCounter'; tableId: string; side: string; delta: -1 | 1 } | null {
  const buttonSize = SEAT_COUNTER_BUTTON_SIZE_PX / scale;
  const hitPad = 8 / scale;
  const halfW = buttonSize / 2 + hitPad;
  const halfH = buttonSize * 1.2 + hitPad;
  if (
    localX < cx - halfW ||
    localX > cx + halfW ||
    localY < cy - halfH ||
    localY > cy + halfH
  ) {
    return null;
  }
  const delta: -1 | 1 = localY <= cy ? 1 : -1;
  return { kind: 'adjustCounter', tableId, side, delta };
}

const DEFAULT_SEAT_RADIUS = 28;
const DEFAULT_INSIDE_OFFSET = 12;
const DEFAULT_OUTSIDE_OFFSET = 18;

export function hexToNumber(hex: string): number {
  const h = hex.replace('#', '').trim();
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return (r << 16) | (g << 8) | b;
  }
  const n = parseInt(h, 16);
  return Number.isNaN(n) ? 0xffffff : n;
}

export function getSeatRadius(table: TableConfig): number {
  return table.seatSize ?? DEFAULT_SEAT_RADIUS;
}

export function getSeatNumberPositions(
  table: TableConfig,
  seatRadius: number
): Map<number, { numberX: number; numberY: number; tagX: number; tagY: number }> {
  const seatScale = seatRadius / DEFAULT_SEAT_RADIUS;
  const insideOffset = seatRadius + DEFAULT_INSIDE_OFFSET * seatScale;
  const outsideOffset = seatRadius + DEFAULT_OUTSIDE_OFFSET * seatScale;

  const out = new Map<number, { numberX: number; numberY: number; tagX: number; tagY: number }>();

  for (const seat of table.seating) {
    if (!seat || seat.seatNumber === undefined) {
      continue;
    }

    let numberX = seat.x;
    let numberY = seat.y;
    let tagX = seat.x;
    let tagY = seat.y;

    if (table.type === 'circular' || table.type === 'oval') {
      const distance = Math.sqrt(seat.x * seat.x + seat.y * seat.y);
      if (distance > 0) {
        const directionInX = -seat.x / distance;
        const directionInY = -seat.y / distance;
        numberX = seat.x + directionInX * insideOffset;
        numberY = seat.y + directionInY * insideOffset;
        const directionOutX = seat.x / distance;
        const directionOutY = seat.y / distance;
        tagX = seat.x + directionOutX * outsideOffset;
        tagY = seat.y + directionOutY * outsideOffset;
      }
    } else {
      const tableHalfWidth = table.width / 2;
      const tableHalfHeight = table.height / 2;
      const distanceToTop = Math.abs(seat.y + tableHalfHeight);
      const distanceToBottom = Math.abs(seat.y - tableHalfHeight);
      const distanceToLeft = Math.abs(seat.x + tableHalfWidth);
      const distanceToRight = Math.abs(seat.x - tableHalfWidth);
      const minDistance = Math.min(distanceToTop, distanceToBottom, distanceToLeft, distanceToRight);

      if (minDistance === distanceToTop) {
        numberX = seat.x;
        numberY = seat.y + insideOffset;
        tagX = seat.x;
        tagY = seat.y - outsideOffset;
      } else if (minDistance === distanceToBottom) {
        numberX = seat.x;
        numberY = seat.y - insideOffset;
        tagX = seat.x;
        tagY = seat.y + outsideOffset;
      } else if (minDistance === distanceToLeft) {
        numberX = seat.x + insideOffset;
        numberY = seat.y;
        tagX = seat.x - outsideOffset;
        tagY = seat.y;
      } else {
        numberX = seat.x - insideOffset;
        numberY = seat.y;
        tagX = seat.x + outsideOffset;
        tagY = seat.y;
      }
    }

    out.set(seat.seatNumber, { numberX, numberY, tagX, tagY });
  }

  return out;
}

export type TableHitTarget =
  | { kind: 'seat'; tableId: string; seatNumber: number; rsvpId: string | null }
  | { kind: 'tableBody'; tableId: string }
  | { kind: 'rotationHandle'; tableId: string }
  | { kind: 'resizeCorner'; tableId: string; angleDeg: number }
  | { kind: 'resizeEdge'; tableId: string; edge: 'n' | 's' | 'e' | 'w' }
  | { kind: 'adjustCounter'; tableId: string; side: string; delta: -1 | 1 }
  | { kind: 'viewport' };

export function hitTestPlanner(
  worldX: number,
  worldY: number,
  tables: TableConfig[],
  selectedTableIds: Set<string>,
  scale: number
): TableHitTarget {
  const seatRadiusHit = 24;
  // Same stacking as the canvas draws: selected tables on top, otherwise array order.
  const stacked = [...tables].sort(
    (a, b) => (selectedTableIds.has(a.id) ? 1 : 0) - (selectedTableIds.has(b.id) ? 1 : 0)
  );

  for (let ti = stacked.length - 1; ti >= 0; ti--) {
    const table = stacked[ti];
    const cos = Math.cos((-table.rotation * Math.PI) / 180);
    const sin = Math.sin((-table.rotation * Math.PI) / 180);
    const dx = worldX - table.x;
    const dy = worldY - table.y;
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;

    for (const seat of table.seating) {
      if (!seat || seat.seatNumber === undefined) {
        continue;
      }
      const ddx = localX - seat.x;
      const ddy = localY - seat.y;
      if (Math.sqrt(ddx * ddx + ddy * ddy) <= seatRadiusHit) {
        return { kind: 'seat', tableId: table.id, seatNumber: seat.seatNumber, rsvpId: seat.rsvpId };
      }
    }

    if (selectedTableIds.has(table.id)) {
      const width = table.width;
      const height = table.height;
      const handleSize = 12 / scale;
      const rotY = -height / 2 - 55 / scale;
      if (Math.sqrt(localX * localX + (localY - rotY) * (localY - rotY)) <= handleSize * 1.5) {
        return { kind: 'rotationHandle', tableId: table.id };
      }

      const insetFromEdge = 14 / scale;
      const insetStep = 6 / scale;

      if (table.type === 'circular') {
        const yOff = width / 2 + 25 / scale;
        const adj = hitAdjustBar(localX, localY, 0, yOff, scale, table.id, 'top');
        if (adj) {
          return adj;
        }
      } else if (table.type === 'rectangular') {
        const longEdgesTopBottom = width >= height;
        const topCy = -height / 2 + insetFromEdge + insetStep;
        const botCy = height / 2 - insetFromEdge - insetStep;
        const leftCx = -width / 2 + insetFromEdge + insetStep;
        const rightCx = width / 2 - insetFromEdge - insetStep;
        const rotDeg = table.rotation ?? 0;
        if (longEdgesTopBottom) {
          let adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            0,
            topCy,
            scale,
            table.id,
            'top',
            rotDeg
          );
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            0,
            botCy,
            scale,
            table.id,
            'bottom',
            rotDeg
          );
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarVertical(localX, localY, leftCx, 0, scale, table.id, 'leftEnd');
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarVertical(localX, localY, rightCx, 0, scale, table.id, 'rightEnd');
          if (adj) {
            return adj;
          }
        } else {
          const stagger = longEdgeHorizontalStaggerY(width, height, scale);
          let adj = hitAdjustBarVertical(localX, localY, 0, topCy, scale, table.id, 'top');
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarVertical(localX, localY, 0, botCy, scale, table.id, 'bottom');
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            leftCx,
            -stagger,
            scale,
            table.id,
            'leftEnd',
            rotDeg
          );
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            rightCx,
            stagger,
            scale,
            table.id,
            'rightEnd',
            rotDeg
          );
          if (adj) {
            return adj;
          }
        }
      } else if (table.type === 'square') {
        const longEdgesTopBottom = width >= height;
        const topCy = -height / 2 + insetFromEdge + insetStep;
        const botCy = height / 2 - insetFromEdge - insetStep;
        const leftCx = -width / 2 + insetFromEdge + insetStep;
        const rightCx = width / 2 - insetFromEdge - insetStep;
        const sideY = -height / 4;
        const rotDeg = table.rotation ?? 0;
        if (longEdgesTopBottom) {
          let adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            0,
            topCy,
            scale,
            table.id,
            'top',
            rotDeg
          );
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            0,
            botCy,
            scale,
            table.id,
            'bottom',
            rotDeg
          );
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarVertical(localX, localY, leftCx, sideY, scale, table.id, 'left');
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarVertical(localX, localY, rightCx, sideY, scale, table.id, 'right');
          if (adj) {
            return adj;
          }
        } else {
          const stagger = longEdgeHorizontalStaggerY(width, height, scale);
          let adj = hitAdjustBarVertical(localX, localY, 0, topCy, scale, table.id, 'top');
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarVertical(localX, localY, 0, botCy, scale, table.id, 'bottom');
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            leftCx,
            sideY - stagger,
            scale,
            table.id,
            'left',
            rotDeg
          );
          if (adj) {
            return adj;
          }
          adj = hitAdjustBarHorizontalScreenAligned(
            localX,
            localY,
            rightCx,
            sideY + stagger,
            scale,
            table.id,
            'right',
            rotDeg
          );
          if (adj) {
            return adj;
          }
        }
      }

      if (table.type === 'circular') {
        const radius = Math.min(width, height) / 2;
        for (const angle of [0, 90, 180, 270]) {
          const rad = (angle * Math.PI) / 180;
          const hx = Math.cos(rad) * radius;
          const hy = Math.sin(rad) * radius;
          if (Math.sqrt((localX - hx) ** 2 + (localY - hy) ** 2) <= handleSize * 1.2) {
            return { kind: 'resizeCorner', tableId: table.id, angleDeg: angle };
          }
        }
      } else {
        const thickness = 14 / scale;
        const zones: Array<{ edge: 'n' | 's' | 'e' | 'w'; x: number; y: number; w: number; h: number }> = [
          { edge: 'n', x: -width / 2, y: -height / 2 - thickness / 2, w: width, h: thickness },
          { edge: 's', x: -width / 2, y: height / 2 - thickness / 2, w: width, h: thickness },
          { edge: 'e', x: width / 2 - thickness / 2, y: -height / 2, w: thickness, h: height },
          { edge: 'w', x: -width / 2 - thickness / 2, y: -height / 2, w: thickness, h: height },
        ];
        for (const z of zones) {
          if (
            localX >= z.x &&
            localX <= z.x + z.w &&
            localY >= z.y &&
            localY <= z.y + z.h
          ) {
            return { kind: 'resizeEdge', tableId: table.id, edge: z.edge };
          }
        }
      }
    }

    let inside = false;
    if (table.type === 'circular') {
      const r = Math.min(table.width, table.height) / 2;
      inside = Math.sqrt(localX * localX + localY * localY) <= r;
    } else if (table.type === 'oval') {
      const rx = table.width / 2;
      const ry = table.height / 2;
      inside = (localX * localX) / (rx * rx) + (localY * localY) / (ry * ry) <= 1;
    } else {
      inside =
        localX >= -table.width / 2 &&
        localX <= table.width / 2 &&
        localY >= -table.height / 2 &&
        localY <= table.height / 2;
    }

    if (inside) {
      return { kind: 'tableBody', tableId: table.id };
    }
  }

  return { kind: 'viewport' };
}
